/**
 * `/admin/system-fixtures` — superadmin-only read-only catalog of the
 * pre-canned Playwright login fixtures the planner agent can
 * auto-match against (#914).
 *
 * Visual pattern mirrors `/admin/agents/prompts`: list-as-card with
 * a heading + tagline + table; clicking a row navigates to the
 * single-fixture detail page. No filters / search here because the
 * catalog is intentionally small (today: Login.gov + a smoke fixture)
 * — adding a search input would be noise. If the catalog ever grows
 * past ~15 entries, copy the search/chip pattern from PromptsPage.
 *
 * The list view consumes the EXISTING public `GET /api/system-fixtures`
 * endpoint (the same one feeding the variant credential picker). That
 * payload already carries every field this table renders. The admin
 * endpoint (`/api/admin/system-fixtures/:id`) is only hit on the
 * detail page — where its load-bearing reason for existing
 * (`specTemplate` disclosure) actually matters.
 *
 * Customer-facing visibility: NONE. The `<RequireSuperadmin>` wrapper
 * around `/admin/*` in App.tsx is UX defense-in-depth; server-side
 * `requireSuperadmin` on the detail endpoint is the actual data-
 * protection boundary. The list endpoint is shared with the
 * customer-facing picker — by design it carries no sensitive data
 * (specTemplate is the only sensitive field, and it's omitted there).
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import {
  systemFixturesAPI,
  type SystemFixtureSummary,
} from '@/lib/api'

export function SystemFixturesPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<SystemFixtureSummary[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    void systemFixturesAPI
      .list()
      .then((list) => {
        if (!alive) return
        // Sort by id ascending so the table order stays stable
        // across reloads. The server already sorts by `_id` in
        // listCatalog, but pinning the client-side order keeps the
        // page deterministic if a future endpoint change loosens
        // the server-side ordering guarantee.
        setRows([...list].sort((a, b) => a.id.localeCompare(b.id)))
        setLoadError(null)
      })
      .catch((e: unknown) => {
        if (!alive) return
        setLoadError(e instanceof Error ? e.message : 'Failed to load system fixtures')
      })
      .finally(() => {
        if (!alive) return
        setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [])

  if (loading && !rows) {
    return <p className="text-sm text-muted-foreground">Loading system fixtures…</p>
  }
  if (loadError && !rows) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {loadError}
      </p>
    )
  }
  if (!rows) return null

  return (
    <div className="space-y-6 max-w-full">
      <div className="text-xs text-muted-foreground">
        <Link to="/admin" className="hover:text-foreground">Global Admin</Link>
        {' · '}
        <span>System Fixtures</span>
      </div>

      <div>
        <h1 className="text-2xl font-semibold">System Fixtures</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pre-canned Playwright login fixtures the planner can auto-match for common IdPs.
          Read-only — adding or editing entries is still a code change. Click any row to
          inspect the spec template + credential requirements.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Catalog ({rows.length} {rows.length === 1 ? 'entry' : 'entries'})</CardTitle>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No system fixtures registered. The boot seeder reconciles
              code-defined fixtures into Mongo on each server start; an
              empty catalog usually means the seeder hasn't run.
            </p>
          ) : (
            // `table-fixed` so every column keeps its assigned width
            // regardless of content length. Without it, a fixture with
            // a long URL pattern blows out the column and shifts the
            // alignment of the others (same lesson PromptsPage learned).
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[18%]">ID</TableHead>
                  <TableHead className="w-[22%]">Name</TableHead>
                  <TableHead className="w-[8%] text-right">Version</TableHead>
                  <TableHead className="w-[28%]">URL patterns</TableHead>
                  <TableHead className="w-[24%]">Required fields</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const href = `/admin/system-fixtures/${encodeURIComponent(row.id)}`
                  // Native `<tr>` isn't keyboard-operable for navigation
                  // — without `tabIndex` + `role` + `onKeyDown`, a
                  // keyboard or screen-reader user can't trigger the
                  // row drill-down (Copilot review on #915). Tab focuses
                  // the row, Enter / Space navigates. Kept on the
                  // `<tr>` itself rather than wrapping a single cell in
                  // an `<a>` so the click target stays the whole row
                  // and the `table-fixed` column geometry isn't disturbed.
                  return (
                    <TableRow
                      key={row.id}
                      onClick={() => navigate(href)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          navigate(href)
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      aria-label={`View ${row.name} fixture (id ${row.id})`}
                      className="cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <TableCell className="font-mono text-xs truncate" title={row.id}>
                        {row.id}
                      </TableCell>
                      <TableCell className="text-sm truncate" title={row.name}>
                        {row.name}
                      </TableCell>
                      <TableCell className="text-right text-xs font-mono">
                        v{row.version}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate" title={row.urlPatterns.join(', ')}>
                        {row.urlPatterns.length === 0
                          ? <span className="italic">—</span>
                          : row.urlPatterns.join(', ')}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate" title={row.requiredFields.join(', ')}>
                        {row.requiredFields.length === 0
                          ? <span className="italic">—</span>
                          : row.requiredFields.join(', ')}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
